import type { ReactNode } from 'react';
import type React from 'react';
import { ClientSideSuspense } from '@liveblocks/react';
import { CommentsProvider } from './CommentsContext';
import { MergedThreadsProvider } from './MergedThreadsProvider';
import { MultiEditorProvider } from 'components/editor/GridDndEditor/TiptapEditor/MultiEditorContext';
import { ActiveEditorProvider } from 'components/editor/GridDndEditor/TiptapEditor/ActiveEditorContext';
import { useIsAuthenticated } from 'hooks/useIsAuthenticated';
import { ApiUserRepository } from 'services/repositories/implementations/ApiUserRepository';
import { useCurrentUserQuery } from 'hooks/useCurrentUserQuery';
import { ThreadPositionProvider } from 'components/editor/GridDndEditor/TiptapEditor/contexts/ThreadPositionContext';
import { useViewerInfo } from 'components/preview/identify-viewer/useViewerInfo';
import { useUndoRedoKeyboardShortcuts } from '../components/editor/hooks/useUndoRedoKeyboardShortcuts.ts';
import { isValidEmail } from 'utils/isValidEmail';
import { isValidUuid } from 'utils/isValidUuid';
import { ContentType } from 'utils/getContentType';

interface LiveblocksProviderProps {
  children: ReactNode;
  contentId: string;
  contentType: ContentType;
  isPrivate?: boolean;
}

export const GUEST_EMAIL = 'guest@example.com';

const getPreviewViewerEmail = (
  isPrivate: boolean,
  viewerEmail: string | null,
  commentsStatus: 'default' | 'pending' | 'completed',
  queryResultEmail?: string
): string | undefined => {
  if (isPrivate) {
    return undefined;
  }

  if (commentsStatus === 'completed') {
    return viewerEmail || '';
  }

  return queryResultEmail || viewerEmail || GUEST_EMAIL;
};

export const LiveblocksProvider: React.FC<LiveblocksProviderProps> = ({ children, contentId, contentType, isPrivate = true }) => {
  const isAuthenticated = useIsAuthenticated() === 'authenticated';
  const queryResult = useCurrentUserQuery(new ApiUserRepository(), { enabled: !isPrivate ? isAuthenticated : true });
  const { viewer } = useViewerInfo();

  const validatedViewerEmail = viewer.email && isValidEmail(viewer.email) && viewer.commentsStatus === 'completed' ? viewer.email : null;
  const previewViewerEmail = getPreviewViewerEmail(isPrivate, validatedViewerEmail, viewer.commentsStatus, queryResult?.data?.email);
  // viewer.id isn't always a UUID (a known user's is their plain account id),
  // but Liveblocks' viewerId requires one - only forward it for anonymous viewers.
  const liveblocksViewerId = isValidUuid(viewer.id) ? viewer.id : undefined;
  useUndoRedoKeyboardShortcuts();

  return (
    <ActiveEditorProvider>
      <MultiEditorProvider>
        {contentType === ContentType.SECTION ? (
          children
        ) : (
          <MergedThreadsProvider
            contentId={contentId}
            contentType={contentType}
            isPrivate={isPrivate}
            userEmail={isPrivate ? queryResult?.data?.email : previewViewerEmail}
            viewerId={liveblocksViewerId}
          >
            <ClientSideSuspense fallback={null}>
              <CommentsProvider
                isPrivate={isPrivate}
                currentUserEmail={queryResult?.data?.email}
                currentUserId={queryResult?.data?.id != null ? String(queryResult.data.id) : undefined}
              >
                <ThreadPositionProvider>{children}</ThreadPositionProvider>
              </CommentsProvider>
            </ClientSideSuspense>
          </MergedThreadsProvider>
        )}
      </MultiEditorProvider>
    </ActiveEditorProvider>
  );
};
